import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import PaymentDialog from "@/components/PaymentDialog";
import CariForm from "@/components/CariForm";
import OdemeSozuDialog from "@/components/OdemeSozuDialog";
import BorcluGorusmeleri from "@/components/BorcluGorusmeleri";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmt, fmtDate, DURUM_COLORS } from "@/lib/format";
import { ArrowLeft, Wallet, CheckCircle2, Plus, Phone, Trash2, Pencil, FileText, Gavel } from "lucide-react";

export default function CariCustomer() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [payFor, setPayFor] = useState(null);
  const [showAddVade, setShowAddVade] = useState(false);
  const [promiseFor, setPromiseFor] = useState(null);
  const [gorusmeler, setGorusmeler] = useState([]);
  const [editPromise, setEditPromise] = useState(null);
  const [agents, setAgents] = useState([]);
  const [editRec, setEditRec] = useState(null);
  const [icraBusy, setIcraBusy] = useState(false);
  const [showIcraSelect, setShowIcraSelect] = useState(false);
  const canEdit = user?.email === "karakoctekhukuk@gmail.com";

  const saveTaksit = async (payload) => {
    try { await api.put(`/cari/${editRec.id}/duzenle`, payload); toast.success("Sözleşme bilgisi güncellendi"); setEditRec(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
  };

  const load = useCallback(() => {
    api.get(`/cari/by-customer/${encodeURIComponent(name)}`).then((res) => setData(res.data));
    api.get(`/cari/musteri-gorusme`, { params: { musteri: name } }).then((res) => setGorusmeler(res.data || [])).catch(() => {});
  }, [name]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user?.role === "admin" || user?.cari_yetkili) api.get("/cari/yetkili-options").then((r) => setAgents(r.data || [])).catch(() => {});
  }, [user]);

  const setYetkili = async (yetkili) => {
    try { await api.post("/cari/musteri-yetkili", { musteri: decodeURIComponent(name), yetkili }); toast.success("Yetkili güncellendi"); load(); }
    catch (e) { toast.error("Güncellenemedi"); }
  };

  const acIcra = () => {
    if (data?.cok_sozlesme) { setShowIcraSelect(true); return; }
    doIcra(null);
  };

  const doIcra = async (sozlesme_ids) => {
    const m = decodeURIComponent(name);
    const secim = sozlesme_ids && sozlesme_ids.length
      ? `Seçilen ${sozlesme_ids.length} sözleşme`
      : "Tüm sözleşmeler";
    if (!window.confirm(`${m} için İcra takibi açılsın mı?\n\n${secim} taşınacak. Ödenmemiş vadeler HUKUK BİRİMİ'ne alınır, borçlular ve (varsa) senet görseli/bilgileri İcra föyüne otomatik taşınır.`)) return;
    setShowIcraSelect(false);
    setIcraBusy(true);
    try {
      const { data: res } = await api.post("/cari/musteri-to-icra", { musteri: m, sozlesme_ids: sozlesme_ids || null });
      toast.success(`İcra föyü açıldı — ${res.vade_sayisi} vade işlendi`);
      navigate(`/icra/${res.icra_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "İcra takibi açılamadı"); }
    finally { setIcraBusy(false); }
  };

  if (!data) return <Layout><div className="py-24 text-center text-[#8C8377]" data-testid="cari-customer-loading">Yükleniyor...</div></Layout>;

  const s = data?.summary || {};
  const records = data?.records || [];
  const sozlesmeler = data?.sozlesmeler || [];
  const promises = records.flatMap((r) => (r.odeme_sozleri || []).map((p, idx) => ({ ...p, _rid: r.id, _vade: r.vade_tarihi, _index: idx })));

  const addPromise = async (payload) => {
    try {
      const arr = Array.isArray(payload) ? payload : [payload];
      for (const p of arr) await api.post(`/cari/${promiseFor.id}/odeme-sozu`, p);
      toast.success(arr.length > 1 ? `${arr.length} taksitli ödeme sözü eklendi` : "Ödeme sözü eklendi");
      setPromiseFor(null); load();
    } catch (e) { toast.error("Eklenemedi"); }
  };

  const addCariGorusme = async (payload) => {
    try { await api.post(`/cari/musteri-gorusme`, { ...payload, musteri: name }); toast.success("Görüşme eklendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); }
  };
  const delCariGorusme = async (gid) => {
    try { await api.delete(`/cari/musteri-gorusme/${gid}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error("Silinemedi"); }
  };
  const deletePromise = async (rid, index) => {
    try { await api.delete(`/cari/${rid}/odeme-sozu/${index}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const saveEditPromise = async (payload) => {
    try {
      const res = await api.put(`/cari/${editPromise._rid}/odeme-sozu/${editPromise._index}`, payload);
      if (res.data?.pending) toast.success("Düzenleme talebiniz yönetici onayına gönderildi");
      else toast.success("Ödeme sözü güncellendi");
      setEditPromise(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
  };

  const fullPay = async (r) => {
    if (r.kalan <= 0) return;
    if (!window.confirm(`${r.musteri} — ${fmtDate(r.vade_tarihi)} vadesi için kalan ${fmt(r.kalan)} tam ödeme olarak işlensin mi?`)) return;
    try {
      await api.post(`/cari/${r.id}/payments`, { tarih: new Date().toISOString().slice(0, 10), tutar: r.kalan });
      toast.success("Tam ödeme işlendi");
      load();
    } catch (e) {
      toast.error("Ödeme işlenemedi");
    }
  };

  const handleAddVade = async (payload) => {
    try {
      await api.post("/cari", payload);
      toast.success("Yeni vade eklendi");
      setShowAddVade(false);
      load();
    } catch (e) {
      toast.error("Vade eklenemedi");
    }
  };

  return (
    <Layout>
      <button onClick={() => navigate(-1)} data-testid="cari-customer-back" className="flex items-center gap-1.5 text-sm text-[#8C8377] hover:text-[#26231E] mb-4 transition-colors duration-150">
        <ArrowLeft className="w-4 h-4" /> Geri
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">CARİ MÜŞTERİ HESABI</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="cari-customer-name">
            {decodeURIComponent(name)}
          </h1>
          {(() => { const c = records[0] || {}; const parts = []; if (c.tc_no) parts.push(`TC: ${c.tc_no}`); if (c.telefon) parts.push(`Tel: ${c.telefon}`); if (c.urun) parts.push(`Ürün: ${c.urun}`); return (parts.length || c.adres || c.ikinci_borclu_ad || (c.borclular && c.borclular.length > 0)) ? (
            <div className="mt-2 text-sm text-[#8C8377] space-y-0.5" data-testid="cari-customer-info">
              {parts.length > 0 && <div>{parts.join(" · ")}</div>}
              {c.adres && <div>Adres: {c.adres}</div>}
              {(c.borclular && c.borclular.length > 0) ? (
                <div className="text-[#B8933F] font-medium" data-testid="cari-borclular">Diğer Borçlular: {c.borclular.map((b, i) => `${b.ad}${b.tc ? ` (TC: ${b.tc})` : ""}`).join(" · ")}</div>
              ) : c.ikinci_borclu_ad ? (
                <div className="text-[#B8933F] font-medium">2. Borçlu: {c.ikinci_borclu_ad}{c.ikinci_borclu_tc ? ` (TC: ${c.ikinci_borclu_tc})` : ""}</div>
              ) : null}
            </div>
          ) : null; })()}
        </div>
        <div className="flex items-end gap-2">
          <div data-testid="cari-yetkili-box">
            <label className="kicker block mb-1.5">YETKİLİ</label>
            {(isAdmin || user?.cari_yetkili) ? (
              <select
                value={records[0]?.yetkili || ""}
                onChange={(e) => setYetkili(e.target.value)}
                data-testid="cari-yetkili-select"
                className="px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm focus:outline-none focus:border-[#B8933F] min-w-[180px]"
              >
                <option value="">— Atanmamış —</option>
                {agents.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
                {records[0]?.yetkili && !agents.some((u) => u.email === records[0].yetkili) && <option value={records[0].yetkili}>{records[0].yetkili}</option>}
              </select>
            ) : (
              <div className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-[#F3ECDD] min-w-[180px]" data-testid="cari-yetkili-value">{records[0]?.yetkili || "— Atanmamış —"}</div>
            )}
          </div>
          <button onClick={() => setShowAddVade(true)} data-testid="add-vade-button" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">
            <Plus className="w-4 h-4" /> Vade Ekle
          </button>
          {isAdmin && (
            <button onClick={acIcra} disabled={icraBusy} data-testid="cari-ac-icra-button" className="flex items-center gap-1.5 px-4 py-2 bg-[#A63A3A] hover:bg-[#8F2F27] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60">
              <Gavel className="w-4 h-4" /> {icraBusy ? "Açılıyor…" : "İcra Takibi Aç"}
            </button>
          )}
        </div>
      </div>

      {(() => {
        const r0 = records[0] || {};
        const metas = [...(r0.senet ? [r0.senet] : []), ...((r0.senetler) || [])];
        const canAddSenet = isAdmin || user?.role === "user";
        return (
          <div className="bg-white border border-[#2F6E4E]/40 rounded-sm p-4 mb-6" data-testid="cari-senet-card">
            <div className="flex items-center justify-between mb-2">
              <div className="kicker flex items-center gap-1.5 text-[#2F6E4E]"><FileText className="w-3.5 h-3.5" /> SÖZLEŞME SENETLERİ {metas.length > 1 ? `(${metas.length})` : ""}</div>
              {canAddSenet && (
                <>
                  <input id="cari-senet-ekle-input" type="file" accept="image/*,.pdf" className="hidden" data-testid="cari-senet-ekle-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; e.target.value = "";
                      if (!f) return;
                      const fd = new FormData(); fd.append("file", f);
                      const tid = toast.loading("Senet kırpılıyor…");
                      try {
                        const { data: res } = await api.post(`/cari/${r0.id}/senet-ekle`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        toast.success(res.slot === "senet" ? "Senet eklendi" : "İkinci/ek senet eklendi", { id: tid });
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || "Senet eklenemedi", { id: tid }); }
                    }} />
                  <label htmlFor="cari-senet-ekle-input" data-testid="cari-senet-ekle-button"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2F6E4E] hover:bg-[#255c40] text-white rounded-sm text-xs font-medium cursor-pointer transition-colors duration-150">
                    <Plus className="w-3.5 h-3.5" /> Senet Ekle (Sözleşmeden Kırp)
                  </label>
                </>
              )}
            </div>
            {metas.length === 0 ? (
              <p className="text-sm text-[#8C8377]">Bu cari için henüz senet yok. Sözleşme görseli/PDF yükleyip senedi otomatik kırpın. Bazı borçluların birden fazla senedi olabilir — her yüklemede yeni senet eklenir.</p>
            ) : (
              <div className="flex flex-wrap gap-4" data-testid="cari-senet-list">
                {metas.map((sn, idx) => (
                  <div key={idx} className="border border-[#E7DECC] rounded-sm p-2" data-testid={`cari-senet-item-${idx}`}>
                    <div className="text-xs text-[#8C8377] mb-1">{idx + 1}. Senet {sn.vade_tarihi ? `· Vade ${fmtDate(sn.vade_tarihi)}` : ""} {sn.tutar ? `· ${fmt(sn.tutar)}` : ""}</div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cari/${r0.id}/senet/view?i=${idx}`} target="_blank" rel="noopener noreferrer" data-testid={`cari-senet-link-${idx}`}>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/cari/${r0.id}/senet/view?i=${idx}`} alt={`Senet ${idx + 1}`} className="max-h-64 w-auto rounded-sm border border-[#E7DECC] hover:opacity-90 transition-opacity duration-150" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Stat label="Vade Sayısı" value={s.kayit_sayisi ?? 0} raw testId="cari-stat-vade-sayisi" />
        <Stat label="Toplam Borç" value={fmt(s.toplam_borc)} testId="cari-stat-borc" />
        <Stat label="Toplam Alış (AF)" value={fmt(s.toplam_alis)} testId="cari-stat-af" />
        <Stat label="Toplam Satış (SF)" value={fmt(s.toplam_satis)} testId="cari-stat-sf" />
        <Stat label="Kâr" value={fmt((s.toplam_satis || 0) - (s.toplam_alis || 0))} color={((s.toplam_satis || 0) - (s.toplam_alis || 0)) >= 0 ? "#2F6E4E" : "#A63A3A"} testId="cari-stat-kar" />
        <Stat label="Peşinat" value={fmt(s.toplam_onceki_tahsilat)} color="#7C5E9B" testId="cari-stat-pesinat" />
        <Stat label="Ödenen" value={fmt(s.toplam_odenen)} color="#2F6E4E" testId="cari-stat-odenen" />
        <Stat label="Kalan" value={fmt(s.toplam_kalan)} color={s.toplam_kalan > 0 ? "#A63A3A" : "#2F6E4E"} testId="cari-stat-kalan" />
      </div>

      {sozlesmeler.length === 0 ? (
        <div className="bg-white border border-[#E7DECC] rounded-sm p-10 text-center text-[#8C8377]" data-testid="cari-customer-empty">Kayıt yok.</div>
      ) : sozlesmeler.map((soz) => {
        const rows = records.filter((r) => (r.sozlesme_gid || "grp|-") === soz.sozlesme_gid);
        return (
          <div key={soz.sozlesme_gid} className="mb-6" data-testid={`sozlesme-block-${soz.no}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2 px-1">
              <div>
                <div className="kicker text-[#B8933F] flex items-center gap-2">
                  {soz.label}
                  {soz.icraya_aktarildi && <span className="px-2 py-0.5 rounded-sm bg-[#A63A3A]/10 text-[#A63A3A] text-[10px] font-semibold">İCRAYA TAŞINDI</span>}
                </div>
                <div className="font-heading font-semibold text-[#26231E]" data-testid={`sozlesme-urun-${soz.no}`}>{soz.urun || "Ürün belirtilmemiş"} <span className="text-[#8C8377] font-normal text-sm">· {soz.vade_sayisi} vade</span></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <MiniStat label="Satış (SF)" value={fmt(soz.toplam_satis)} testId={`sozlesme-sf-${soz.no}`} />
                <MiniStat label="Peşinat" value={fmt(soz.toplam_pesinat)} color="#7C5E9B" testId={`sozlesme-pesinat-${soz.no}`} />
                <MiniStat label="Ödenen" value={fmt(soz.toplam_odenen)} color="#2F6E4E" testId={`sozlesme-odenen-${soz.no}`} />
                <MiniStat label="Kalan" value={fmt(soz.toplam_kalan)} color={soz.toplam_kalan > 0 ? "#A63A3A" : "#2F6E4E"} testId={`sozlesme-kalan-${soz.no}`} />
              </div>
            </div>
            <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto" data-testid={`cari-customer-table-${soz.no}`}>
              <table className="w-full text-sm">
                <thead className="bg-[#F3ECDD]">
                  <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
                    <th className="px-3 py-2.5 kicker text-left">Vade</th>
                    <th className="px-3 py-2.5 kicker text-left">Açıklama</th>
                    <th className="px-3 py-2.5 kicker text-right">Tutar</th>
                    <th className="px-3 py-2.5 kicker text-right">Ödenen</th>
                    <th className="px-3 py-2.5 kicker text-right">Kalan</th>
                    <th className="px-3 py-2.5 kicker text-left">Gecikme</th>
                    <th className="px-3 py-2.5 kicker text-left">Durum</th>
                    <th className="px-3 py-2.5 kicker text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-[#8C8377]">Vade yok.</td></tr>
                  ) : rows.map((r, i) => (
                    <VadeRow key={r.id} r={r} i={i} canEdit={canEdit}
                      onPay={() => setPayFor(r)} onFullPay={() => fullPay(r)}
                      onPromise={() => setPromiseFor(r)} onEdit={() => setEditRec(r)} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="mt-6 bg-white border border-[#E7DECC] rounded-sm" data-testid="cari-promises">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
          <Phone className="w-4 h-4 text-[#7C5E9B]" />
          <div className="kicker">ÖDEME SÖZÜ / CALL CENTER GÖRÜŞMELERİ</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              <th className="px-4 py-2.5 kicker text-left">Görüşme</th>
              <th className="px-4 py-2.5 kicker text-left">İlgili Vade</th>
              <th className="px-4 py-2.5 kicker text-left">Temsilci</th>
              <th className="px-4 py-2.5 kicker text-left">Mahiyet</th>
              <th className="px-4 py-2.5 kicker text-left">Taahhüt Tarihi</th>
              <th className="px-4 py-2.5 kicker text-right">Taahhüt Tutarı</th>
              <th className="px-4 py-2.5 kicker text-left">Not</th>
              <th className="px-4 py-2.5 kicker text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {promises.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-[#8C8377]">Kayıt yok. Bir vade satırındaki "Söz" ile görüşme kaydı ekleyin.</td></tr>
            ) : promises.map((p) => (
              <tr key={`${p._rid}-${p._index}`} className="border-b border-[#E7DECC]" data-testid={`cari-promise-row-${p._rid}-${p._index}`}>
                <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDate(p.tarih)}</td>
                <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDate(p._vade)}</td>
                <td className="px-4 py-2 whitespace-nowrap">{p.temsilci || "-"}</td>
                <td className="px-4 py-2 whitespace-nowrap">{p.mahiyet ? <span className="px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]">{p.mahiyet}</span> : "-"}</td>
                <td className="px-4 py-2 tabular-nums whitespace-nowrap">{p.taahhut_tarihi ? fmtDate(p.taahhut_tarihi) : "-"}</td>
                <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{p.taahhut_tutari ? fmt(p.taahhut_tutari) : "-"}</td>
                <td className="px-4 py-2 text-[#8C8377]">{p.aciklama || "-"}</td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditPromise(p)} data-testid={`cari-promise-edit-${p._rid}-${p._index}`} className="p-1.5 text-[#8C8377] hover:text-[#B8933F] rounded-sm transition-colors duration-150" title={isAdmin ? "Düzenle" : "Düzenle (yönetici onayına gider)"}><Pencil className="w-4 h-4" /></button>
                    {isAdmin && (
                      <button onClick={() => deletePromise(p._rid, p._index)} data-testid={`cari-promise-del-${p._rid}-${p._index}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] rounded-sm transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {payFor && (
        <PaymentDialog
          record={payFor}
          onClose={() => { setPayFor(null); load(); }}
          onChanged={(updated) => setPayFor(updated)}
        />
      )}
      <div className="mt-6" data-testid="cari-gorusmeler-wrap">
        <BorcluGorusmeleri items={gorusmeler} canAdd={true} isAdmin={isAdmin} onAdd={addCariGorusme} onDelete={delCariGorusme} />
      </div>

      {promiseFor && (
        <OdemeSozuDialog title={`Ödeme Sözü — ${fmtDate(promiseFor.vade_tarihi)} vadesi`} onClose={() => setPromiseFor(null)} onSubmit={addPromise} />
      )}
      {editPromise && (
        <OdemeSozuDialog
          title="Ödeme Sözü Düzenle"
          initial={editPromise}
          submitLabel={isAdmin ? "Güncelle" : "Onaya Gönder"}
          note={isAdmin ? "" : "Bu değişiklik yönetici onayına gönderilecektir. Onaylanana kadar mevcut kayıt değişmez."}
          onClose={() => setEditPromise(null)}
          onSubmit={saveEditPromise}
        />
      )}
      {showAddVade && (
        <CariForm fixedMusteri={decodeURIComponent(name)} onClose={() => setShowAddVade(false)} onSubmit={handleAddVade} />
      )}
      {editRec && (
        <TaksitEditDialog rec={editRec} onClose={() => setEditRec(null)} onSubmit={saveTaksit} />
      )}
      {showIcraSelect && (
        <IcraSelectDialog sozlesmeler={sozlesmeler} busy={icraBusy}
          onClose={() => setShowIcraSelect(false)} onConfirm={(ids) => doIcra(ids)} />
      )}
    </Layout>
  );
}

function Stat({ label, value, raw, color, testId }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid={testId}>
      <div className="kicker mb-1.5">{label}</div>
      <div className="font-heading font-bold text-lg tabular-nums" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, color, testId }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm px-3 py-1.5 text-center min-w-[96px]" data-testid={testId}>
      <div className="kicker text-[10px]">{label}</div>
      <div className="font-heading font-bold text-sm tabular-nums" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

function VadeRow({ r, i, canEdit, onPay, onFullPay, onPromise, onEdit }) {
  const dc = DURUM_COLORS[r.durum] || "#8C8377";
  return (
    <tr className={`border-b border-[#E7DECC] ${i % 2 === 1 ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`cari-customer-row-${r.id}`}>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtDate(r.vade_tarihi)}</td>
      <td className="px-3 py-2 text-[#8C8377]">{r.aciklama || "-"}</td>
      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{fmt(r.tutar)}</td>
      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-[#2F6E4E]">{fmt(r.odenen)}</td>
      <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${r.kalan > 0 ? "text-[#A63A3A]" : "text-[#2F6E4E]"}`}>{fmt(r.kalan)}</td>
      <td className="px-3 py-2 whitespace-nowrap" data-testid={`cari-customer-gecikme-${r.id}`}>
        {(() => {
          if (!(r.kalan > 0) || !r.vade_tarihi) return <span className="text-[#8C8377] text-xs">-</span>;
          const t = new Date(); t.setHours(0, 0, 0, 0);
          const d = new Date(r.vade_tarihi); d.setHours(0, 0, 0, 0);
          const gun = Math.round((t - d) / 86400000);
          if (gun > 0) return <span className="px-2 py-0.5 rounded-sm bg-[#A63A3A]/10 text-[#A63A3A] text-xs font-semibold">{gun} gün gecikmiş</span>;
          if (gun === 0) return <span className="px-2 py-0.5 rounded-sm bg-[#B8933F]/10 text-[#B8933F] text-xs font-semibold">Bugün</span>;
          return <span className="text-[#8C8377] text-xs">{Math.abs(gun)} gün var</span>;
        })()}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ backgroundColor: `${dc}1a`, color: dc }} data-testid={`cari-customer-durum-${r.id}`}>{r.durum}</span>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <div className="flex justify-end gap-1">
          <button onClick={onPay} data-testid={`cari-customer-pay-${r.id}`} className="flex items-center gap-1 px-2 py-1 text-[#B8933F] hover:bg-[#EFE7D6] border border-[#E7DECC] rounded-sm text-xs font-medium transition-colors duration-150">
            <Wallet className="w-3.5 h-3.5" /> Ödeme
          </button>
          <button onClick={onFullPay} disabled={r.kalan <= 0} data-testid={`cari-customer-fullpay-${r.id}`} className="flex items-center gap-1 px-2 py-1 text-[#2F6E4E] hover:bg-[#EFE7D6] border border-[#E7DECC] rounded-sm text-xs font-medium transition-colors duration-150 disabled:opacity-40">
            <CheckCircle2 className="w-3.5 h-3.5" /> Tam Öde
          </button>
          <button onClick={onPromise} data-testid={`cari-customer-promise-${r.id}`} className="flex items-center gap-1 px-2 py-1 text-[#7C5E9B] hover:bg-[#EFE7D6] border border-[#E7DECC] rounded-sm text-xs font-medium transition-colors duration-150">
            <Phone className="w-3.5 h-3.5" /> Söz
          </button>
          {canEdit && (
            <button onClick={onEdit} data-testid={`cari-customer-edit-${r.id}`} className="flex items-center gap-1 px-2 py-1 text-[#B8933F] hover:bg-[#EFE7D6] border border-[#E7DECC] rounded-sm text-xs font-medium transition-colors duration-150">
              <Pencil className="w-3.5 h-3.5" /> Düzenle
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function IcraSelectDialog({ sozlesmeler, busy, onClose, onConfirm }) {
  const list = (sozlesmeler || []).filter((sz) => !sz.icraya_aktarildi);
  const [sel, setSel] = useState(() => list.map((sz) => sz.sozlesme_gid));
  const toggle = (gid) => setSel((p) => (p.includes(gid) ? p.filter((x) => x !== gid) : [...p, gid]));
  const total = list.filter((sz) => sel.includes(sz.sozlesme_gid)).reduce((a, sz) => a + (sz.toplam_kalan || 0), 0);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-lg" data-testid="icra-select-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DECC]">
          <span className="font-heading font-bold flex items-center gap-2"><Gavel className="w-4 h-4 text-[#A63A3A]" /> İcraya Aktarılacak Sözleşmeler</span>
          <button onClick={onClose} className="text-[#8C8377] hover:text-[#26231E] text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-2">
          <p className="text-sm text-[#8C8377] mb-2">Bu müşterinin birden fazla sözleşmesi var. İcraya taşımak istediklerinizi seçin.</p>
          {list.map((sz) => (
            <label key={sz.sozlesme_gid} data-testid={`icra-select-${sz.no}`}
              className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors duration-150 ${sel.includes(sz.sozlesme_gid) ? "border-[#A63A3A] bg-[#A63A3A]/5" : "border-[#E7DECC] hover:bg-[#F3ECDD]"}`}>
              <input type="checkbox" checked={sel.includes(sz.sozlesme_gid)} onChange={() => toggle(sz.sozlesme_gid)} data-testid={`icra-select-cb-${sz.no}`} className="w-4 h-4 accent-[#A63A3A]" />
              <div className="flex-1">
                <div className="font-medium text-[#26231E]">{sz.label} — {sz.urun || "Ürün belirtilmemiş"}</div>
                <div className="text-xs text-[#8C8377]">{sz.vade_sayisi} vade · Kalan: <b className="text-[#A63A3A]">{fmt(sz.toplam_kalan)}</b></div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[#E7DECC]">
          <div className="text-sm">Seçili kalan toplam: <b className="text-[#A63A3A] tabular-nums">{fmt(total)}</b></div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm">İptal</button>
            <button onClick={() => onConfirm(sel)} disabled={busy || sel.length === 0} data-testid="icra-select-confirm"
              className="px-5 py-2 bg-[#A63A3A] hover:bg-[#8F2F27] text-white rounded-sm text-sm font-medium disabled:opacity-50">
              {busy ? "Açılıyor…" : `İcra Takibi Aç (${sel.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaksitEditDialog({ rec, onClose, onSubmit }) {
  const [f, setF] = useState({
    vade_tarihi: (rec.vade_tarihi || "").slice(0, 10),
    aciklama: rec.aciklama || "",
    tutar: rec.tutar ?? "",
    alis_fiyati: rec.alis_fiyati ?? "",
    satis_fiyati: rec.satis_fiyati ?? "",
    onceki_tahsilat: rec.onceki_tahsilat ?? "",
    urun: rec.urun || "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const N = (v) => (v === "" || v == null ? 0 : parseFloat(String(v).replace(",", ".")) || 0);
  const submit = () => {
    onSubmit({
      vade_tarihi: f.vade_tarihi || null,
      aciklama: f.aciklama,
      tutar: N(f.tutar),
      alis_fiyati: N(f.alis_fiyati),
      satis_fiyati: N(f.satis_fiyati),
      onceki_tahsilat: N(f.onceki_tahsilat),
      urun: f.urun,
    });
  };
  const Fld = ({ label, k, type = "text", full }) => (
    <div className={full ? "col-span-2" : ""}>
      <label className="kicker block mb-1">{label}</label>
      <input type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`taksit-edit-${k}`} className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
    </div>
  );
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-lg" data-testid="taksit-edit-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DECC]">
          <span className="font-heading font-bold">Sözleşme / Taksit Düzenle</span>
          <button onClick={onClose} className="text-[#8C8377] hover:text-[#26231E]"><Pencil className="w-0 h-0" /><span className="text-xl leading-none">×</span></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Fld label="ÜRÜN / AÇIKLAMA" k="urun" full />
          <Fld label="VADE TARİHİ" k="vade_tarihi" type="date" />
          <Fld label="TAKSİT TUTARI" k="tutar" type="number" />
          <Fld label="ALIŞ (AF)" k="alis_fiyati" type="number" />
          <Fld label="SATIŞ (SF)" k="satis_fiyati" type="number" />
          <Fld label="PEŞİNAT" k="onceki_tahsilat" type="number" />
          <Fld label="VADE AÇIKLAMASI" k="aciklama" full />
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E7DECC]">
          <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm">İptal</button>
          <button onClick={submit} data-testid="taksit-edit-save" className="px-5 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium">Kaydet</button>
        </div>
      </div>
    </div>
  );
}
